import { useLayoutEffect, type RefObject } from "react";

/**
 * Publishes an element's rendered height as a CSS custom property on <html>, so elements further
 * down the page can stick *below* it without anyone hardcoding a pixel offset.
 *
 * Four regions have to stack: app header, page title, filter toolbar, table column headers. Only
 * the app header has a fixed height (h-16). The title grows when its description wraps, and the
 * toolbar grows a lot at 360px, where the search field, two date inputs and Export CSV wrap onto
 * separate lines. A literal `top-[136px]` would be wrong at most widths and on most pages.
 *
 * ResizeObserver keeps the value correct through wrapping, font loading and window resizes.
 *
 * The property is set on documentElement rather than passed through context because the consumer
 * is a `top-[calc(...)]` class on a <thead> deep inside Table, and threading a number through
 * every page and column definition to reach it would be far more invasive than one variable.
 * Only one sticky title and one sticky toolbar exist per page, so there is no ambiguity about
 * which element owns the value.
 */
export function useStickyOffset(
  ref: RefObject<HTMLElement | null>,
  cssVar: string,
  enabled = true,
): void {
  // Layout effect, not effect: the variable must be readable before the browser paints, or the
  // table header flashes at the wrong offset on first render.
  useLayoutEffect(() => {
    const node = ref.current;
    if (!enabled || !node) return;

    const publish = () => {
      document.documentElement.style.setProperty(cssVar, `${node.offsetHeight}px`);
    };
    publish();

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(publish);
      observer.observe(node);
    }

    return () => {
      observer?.disconnect();
      // Reset rather than leave a stale height behind: navigating from a page with a toolbar to
      // one without would otherwise leave its column headers stuck a toolbar's height too low.
      document.documentElement.style.setProperty(cssVar, "0px");
    };
  }, [ref, cssVar, enabled]);
}

/** Height of the app header (h-16). The one fixed value in the stack. */
export const APP_HEADER_H = "4rem";
export const STICKY_TITLE_VAR = "--sb-sticky-title";
export const STICKY_TOOLBAR_VAR = "--sb-sticky-toolbar";

/**
 * Height budget for the table body scrollport: the viewport, minus the app header, the sticky
 * title, the sticky filter toolbar, and a reserve for the pagination bar plus page padding.
 *
 * dvh rather than vh so mobile browser chrome collapsing does not leave the table taller than
 * the visible area. The 9rem reserve covers the pagination bar (~3.25rem), the gap above it and
 * the page bottom padding.
 */
export const TABLE_BODY_MAX_H = `calc(100dvh - ${APP_HEADER_H} - var(${STICKY_TITLE_VAR}, 0px) - var(${STICKY_TOOLBAR_VAR}, 0px) - 9rem)`;
/** Top offset for the toolbar itself — below the app header and the title. */
export const TOOLBAR_TOP = `calc(${APP_HEADER_H} + var(${STICKY_TITLE_VAR}, 0px))`;
